import { AxiosResponse } from 'axios';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { deleteCalibration as removeCalibration } from 'views/bluviu-nutrition/features/calibrations/api/calibration';
import {
  cloneR2Calibration,
  postR2Calibration,
} from 'views/bluviu-nutrition/features/calibrations/api/r2-calibration';
import { cloneSimpleCalibration, postSimpleCalibration } from 'views/bluviu-nutrition/features/calibrations/api/simple-calibration';
import { CalibrationModel } from 'views/bluviu-nutrition/features/calibrations/types/calibration-model.enum';
import { CalibrationsParams } from 'views/bluviu-nutrition/features/calibrations/types/calibrations-params.type';
import { CloneCalibrationRequest } from 'views/bluviu-nutrition/features/calibrations/types/clone-calibration-request.type';
import { R2Calibration } from 'views/bluviu-nutrition/features/calibrations/types/r2-calibration.type';
import { CreateCalibrationRequest } from '../types/create-calibration-request.type';
import { SimpleCalibration } from '../types/simple-calibration.type';

/**
 * A hook for creating a calibration. It attempts to obfuscate boilerplate code out of a component and only expose what is necessary.
 */
export const useCreateCalibration = () => {
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const intl = useIntl();
  const params = useParams<CalibrationsParams>();

  const createCalibration = usePost<
    SimpleCalibration | R2Calibration,
    { model: CalibrationModel; request: CreateCalibrationRequest }
  >(
    ({ request, model }) =>
      model === CalibrationModel.EFFECTIVE_ENERGY_GT2_MULTI_CAGE
        ? postR2Calibration(params.globalSiteId, request)
        : postSimpleCalibration(params.globalSiteId, request),
    () => {
      console.log('success');

      queryClient.invalidateQueries({
        queryKey: ['getCalibrations']
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // TODO: figure out how clone calibration will work. separate endpoints / single endpoint? infer model based on calibration id?
  const cloneCalibration = usePost<
    SimpleCalibration | R2Calibration,
    { model: CalibrationModel; request: CloneCalibrationRequest }
  >(
    ({ request, model }) =>
      model === CalibrationModel.EFFECTIVE_ENERGY_GT2_MULTI_CAGE
        ? cloneR2Calibration(params.globalSiteId, request)
        : cloneSimpleCalibration(params.globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getCalibrations']
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const deleteCalibration = usePost<AxiosResponse, { model: CalibrationModel; calibrationId: string }>(
    (request) => removeCalibration(params.globalSiteId, request.calibrationId),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  return { createCalibration, cloneCalibration, deleteCalibration };
};
